'use client';

import { Phone, Calendar, Clock, User } from 'lucide-react';
import type { BaseTileProps, SearchResult } from '../types';
import { cn } from '@/lib/utils';
import { humanizeIncidentType } from '@/lib/incidentDisplay';
import { ScoreBadge, SourceBadge } from './BaseTile';

export function CallTile({
  result,
  mode,
  showScore,
  showSource,
  highlighted,
  onClick,
  className,
}: BaseTileProps<SearchResult>) {
  const meta = result.metadata as Record<string, unknown>;
  const caseNumber = meta.caseNumber as string | undefined;
  const officerName = meta.officerName as string | undefined;
  const incidentType = meta.incidentType as string | undefined;
  const duration = meta.duration as number | undefined;
  const startTime = meta.startTime as string | undefined;

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div
      className={cn(
        'px-3 py-2.5 rounded-lg cursor-pointer transition-colors',
        highlighted ? 'bg-amber-500/15' : 'hover:bg-slate-800/50',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-md bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Phone className="w-3.5 h-3.5 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm text-white truncate">
              {caseNumber ? `Case #${caseNumber}` : result.title}
            </h4>
            {incidentType && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 whitespace-nowrap">
                {humanizeIncidentType(incidentType)}
              </span>
            )}
            {showScore && result.score > 0 && <ScoreBadge score={result.score} />}
            {showSource && <SourceBadge source={result.source} />}
          </div>

          {result.preview && mode === 'expanded' && (
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{result.preview}</p>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-slate-500">
            {officerName && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {officerName}
              </span>
            )}
            {startTime && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(startTime).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric',
                })}
              </span>
            )}
            {duration != null && duration > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDuration(duration)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
